import { useMemo } from 'react';
import { Link } from 'react-router';
import {
  type WirelessBssidItem,
  type WirelessClientItem,
  type WirelessRadioItem,
  useDeviceWireless,
} from '@/hooks/useDeviceWireless';
import { Badge } from '@/components/ui/badge';
import { Skeleton } from '@/components/ui/skeleton';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { EmptyState } from '@/components/ui/empty-state';
import {
  ResponsiveTable,
  type ResponsiveTableColumn,
} from '@/components/ui/responsive-table';
import { cn } from '@/lib/utils';

interface DeviceWirelessTabProps {
  deviceId: string;
}

/** A BSSID flattened with its radio's band/channel for the SSID table. */
interface SsidRow extends WirelessBssidItem {
  band: string;
  currentChannel: number | null;
}

// RSSI → coarse quality bucket. Rough rule of thumb: ≥ −60 dBm excellent,
// −60…−72 usable, below that weak.
function signalClass(dbm: number | null): string {
  if (dbm == null) return 'bg-muted text-muted-foreground';
  if (dbm >= -60) return 'bg-sev-4/15 text-sev-4';
  if (dbm >= -72) return 'bg-sev-3/20 text-foreground';
  return 'bg-sev-1/15 text-sev-1';
}

// Radio state, on the same tones device status already uses: healthy green,
// fault red, neutral grey for an answer that is not a verdict. `unknown` is the
// common case — most sources report a radio's channel and power but never its
// state — and it must not read as a fault, while `disabled` is an operator's
// choice rather than a problem. The badge prints the value, so those two stay
// apart in the label even though they share a tone.
const RADIO_STATUS_STYLE: Record<string, string> = {
  up: 'bg-sev-4/15 text-sev-4',
  down: 'bg-sev-1/15 text-sev-1',
  disabled: 'bg-muted text-muted-foreground',
  unknown: 'bg-muted text-muted-foreground',
};

const RADIO_COLUMNS: readonly ResponsiveTableColumn<WirelessRadioItem>[] = [
  {
    key: 'radio',
    label: 'Radio',
    hideMobileLabel: true,
    render: (r) => (
      <span className="font-mono font-medium">{r.radioName ?? `radio ${String(r.radioIndex)}`}</span>
    ),
  },
  {
    key: 'band',
    label: 'Band',
    render: (r) => <Badge className="bg-brand-soft text-brand border-0 font-normal">{r.band}</Badge>,
  },
  {
    key: 'channel',
    label: 'Channel',
    align: 'right',
    render: (r) =>
      r.currentChannel != null ? (
        <span className="font-mono">{r.currentChannel}</span>
      ) : (
        <span className="text-muted-foreground">—</span>
      ),
  },
  {
    key: 'power',
    label: 'Tx power',
    align: 'right',
    hideAt: 'mobile',
    render: (r) =>
      r.transmitPowerDbm != null ? (
        <span className="font-mono">{r.transmitPowerDbm} dBm</span>
      ) : (
        <span className="text-muted-foreground">—</span>
      ),
  },
  {
    key: 'clients',
    label: 'Clients',
    align: 'right',
    render: (r) => <span className="font-mono font-medium">{r.clientCount}</span>,
  },
  {
    key: 'status',
    label: 'Status',
    render: (r) => (
      <Badge
        className={cn(
          'border-0 font-normal capitalize',
          // An unrecognized value is one we cannot interpret, which is neutral
          // for the same reason `unknown` is — never a fault we can't evidence.
          RADIO_STATUS_STYLE[r.status.toLowerCase()] ?? 'bg-muted text-muted-foreground',
        )}
      >
        {r.status}
      </Badge>
    ),
  },
];

const SSID_COLUMNS: readonly ResponsiveTableColumn<SsidRow>[] = [
  {
    key: 'ssid',
    label: 'SSID',
    hideMobileLabel: true,
    render: (b) => <span className="font-medium">{b.ssidName || <span className="text-muted-foreground">(hidden)</span>}</span>,
  },
  {
    key: 'bssid',
    label: 'BSSID',
    render: (b) =>
      b.bssid ? (
        <span className="font-mono text-xs">{b.bssid}</span>
      ) : (
        // Devices managed over an HTTP/JSON API report SSIDs with no BSSID.
        <span className="text-muted-foreground text-xs">—</span>
      ),
  },
  {
    key: 'band',
    label: 'Band',
    render: (b) => (
      <span className="text-sm">
        {b.band}
        {b.currentChannel != null ? <span className="text-muted-foreground"> · ch {b.currentChannel}</span> : null}
      </span>
    ),
  },
  {
    key: 'security',
    label: 'Security',
    hideAt: 'mobile',
    render: (b) => b.securityType ?? <span className="text-muted-foreground">—</span>,
  },
  {
    key: 'vlan',
    label: 'VLAN',
    align: 'right',
    hideAt: 'mobile',
    render: (b) =>
      b.vlanId != null ? <span className="font-mono">{b.vlanId}</span> : <span className="text-muted-foreground">—</span>,
  },
];

/** Friendly label for a client: hostname → OUI vendor → MAC. */
function clientName(c: WirelessClientItem): string {
  return c.hostname ?? c.ouiVendor ?? c.macAddress;
}

const CLIENT_COLUMNS: readonly ResponsiveTableColumn<WirelessClientItem>[] = [
  {
    key: 'client',
    label: 'Client',
    hideMobileLabel: true,
    render: (c) => {
      const name = clientName(c);
      // Only show the MAC as a sub-line when the name isn't already the MAC.
      const showMac = name !== c.macAddress;
      return (
        <div className="flex flex-col">
          {c.endpointId != null ? (
            <Link
              to={`/endpoints/${c.endpointId}`}
              className="font-medium hover:underline"
              onClick={(e) => { e.stopPropagation(); }}
            >
              {name}
            </Link>
          ) : (
            <span className="font-medium">{name}</span>
          )}
          {showMac ? <span className="font-mono text-xs text-muted-foreground">{c.macAddress}</span> : null}
        </div>
      );
    },
  },
  {
    key: 'ssid',
    label: 'SSID',
    render: (c) => c.ssidName ?? <span className="text-muted-foreground">—</span>,
  },
  {
    key: 'band',
    label: 'Band',
    hideAt: 'mobile',
    render: (c) => c.band ?? <span className="text-muted-foreground">—</span>,
  },
  {
    key: 'signal',
    label: 'Signal',
    render: (c) =>
      c.signalStrengthDbm != null ? (
        <Badge className={cn('border-0 font-normal font-mono', signalClass(c.signalStrengthDbm))}>
          {c.signalStrengthDbm} dBm
        </Badge>
      ) : (
        <span className="text-muted-foreground">—</span>
      ),
  },
  {
    key: 'quality',
    label: 'Quality',
    align: 'right',
    hideAt: 'mobile',
    render: (c) =>
      c.signalQuality != null ? (
        <span className="font-mono">{c.signalQuality}</span>
      ) : (
        <span className="text-muted-foreground">—</span>
      ),
  },
  {
    key: 'status',
    label: 'Status',
    render: (c) => (
      <Badge
        className={cn(
          'border-0 font-normal',
          c.associationStatus === 'associated'
            ? 'bg-sev-4/15 text-sev-4'
            : 'bg-muted text-muted-foreground',
        )}
      >
        {c.associationStatus}
      </Badge>
    ),
  },
];

export default function DeviceWirelessTab({ deviceId }: DeviceWirelessTabProps) {
  const { data, isLoading, isError } = useDeviceWireless(deviceId);

  const ssidRows = useMemo<SsidRow[]>(
    () =>
      data
        ? data.radios.flatMap((r) =>
            r.bssids.map((b) => ({ ...b, band: r.band, currentChannel: r.currentChannel })),
          )
        : [],
    [data],
  );

  if (isLoading) {
    return <Skeleton className="h-64 w-full" />;
  }

  if (isError || !data) {
    return (
      <Alert variant="destructive">
        <AlertDescription>Failed to load wireless data.</AlertDescription>
      </Alert>
    );
  }

  const noData = data.radios.length === 0 && data.clients.length === 0;
  if (noData) {
    return (
      <section className="bg-card border border-border rounded-lg p-5">
        <EmptyState
          variant="no-data"
          title="No wireless data yet"
          description="If this is an access point, ensure SNMP is configured and the CISCO-DOT11 / IEEE802dot11 MIBs are reachable. Data refreshes every 5 minutes."
          size="inline"
        />
      </section>
    );
  }

  return (
    <div className="space-y-4 md:space-y-5">
      <section className="bg-card border border-border rounded-lg p-5">
        <div className="flex items-center justify-between mb-3">
          <h2 className="text-sm font-semibold">Radios</h2>
          <span className="text-xs text-muted-foreground">
            {String(data.radios.length)} radio{data.radios.length === 1 ? '' : 's'}
          </span>
        </div>
        {data.radios.length === 0 ? (
          <p className="text-sm text-muted-foreground">No radios reported.</p>
        ) : (
          <ResponsiveTable<WirelessRadioItem>
            columns={RADIO_COLUMNS}
            rows={data.radios}
            rowKey={(r) => r.id}
          />
        )}
      </section>

      <section className="bg-card border border-border rounded-lg p-5">
        <div className="flex items-center justify-between mb-3">
          <h2 className="text-sm font-semibold">SSIDs</h2>
          <span className="text-xs text-muted-foreground">
            {String(ssidRows.length)} BSSID{ssidRows.length === 1 ? '' : 's'}
          </span>
        </div>
        {ssidRows.length === 0 ? (
          <p className="text-sm text-muted-foreground">No SSIDs reported.</p>
        ) : (
          <ResponsiveTable<SsidRow>
            columns={SSID_COLUMNS}
            rows={ssidRows}
            rowKey={(b) => b.id}
          />
        )}
      </section>

      <section className="bg-card border border-border rounded-lg p-5">
        <div className="flex items-center justify-between mb-3">
          <h2 className="text-sm font-semibold">Connected clients</h2>
          <span className="text-xs text-muted-foreground">
            {String(data.clients.length)} client{data.clients.length === 1 ? '' : 's'}
          </span>
        </div>
        {data.clients.length === 0 ? (
          <p className="text-sm text-muted-foreground">No clients currently associated.</p>
        ) : (
          <ResponsiveTable<WirelessClientItem>
            columns={CLIENT_COLUMNS}
            rows={data.clients}
            rowKey={(c) => c.macAddress}
          />
        )}
      </section>
    </div>
  );
}
